import { useState, useEffect } from "react";
import { useDrop } from "react-dnd";
import { getToken, base_url } from "../../config/config";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import FieldConfigDialog from "../../components/CustomForm/FieldsDialog";
import { FormLayout } from "../../components/CustomForm/FormLayout";
import {
  DraggablePaletteItem,
  ItemTypes,
} from "../../components/CustomForm/Draggable";
import { getCustomFormById } from "./api"; // Adjust path if needed
import { TextField } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  Text,
  LetterText,
  CircleCheck,
  ChevronDown,
  Info as InfoIcon,
} from "lucide-react";

export default function CustomFormBuilder() {
  const { id } = useParams();
  const initialFields = [];
  const templates = [
    { type: "text", label: "Text Field", icon: <Text size={18} /> },
    { type: "textarea", label: "Text Area", icon: <LetterText size={18} /> },
    {
      type: "select",
      label: "Select",
      icon: <ChevronDown size={18} />,
    },
    { type: "checkbox", label: "Checkbox", icon: <CircleCheck size={16} /> },
    {
      type: "radio",
      label: "Radio Button",
      icon: <LetterText size={18} />,
    },
  ];

  const [fields, setFields] = useState(initialFields);
  const [pending, setPending] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftType, setDraftType] = useState("text");
  const [draftRequired, setDraftRequired] = useState(false);
  const [formName, setFormName] = useState("");
  const [formType, setFormType] = useState("text");
  const [formDescription, setFormDescription] = useState("");
  const userState = useSelector((state) => state.user);
  const token = getToken();
  const theme = useTheme();

  const fetchForm = async () => {
    if (id) {
      try {
        const response = await getCustomFormById(id);
        // Assuming response.data.fields contains the fields array
        setFields(response.data?.fields || []);
      } catch (err) {
        console.error("Failed to fetch form by id", err);
      }
    }
  };

  useEffect(() => {
    if (id) {
      fetchForm();
    }
  }, [id]);

  // POST to backend
  const handleCreateForm = async () => {
    const payload = {
      fields: fields.map((f, idx) => ({
        label: f.label,
        type: f.type,
        ...(f.options ? { options: f.options.map((o) => o.label) } : {}),
        required: f.required,
        order: idx,
      })),
      createdBy: userState.userId,
    };
    try {
      const res = await fetch(`${base_url}/v1/createCustomForm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      alert("Form created successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to create form: " + err.message);
    }
  };

  const addField = (tmpl) => {
    setFields((prev) => [
      ...prev,
      {
        ...tmpl,
        name: `${tmpl.type}_${prev.length + 1}`,
        label: draftLabel,
        type: draftType,
        required: draftRequired,
      },
    ]);
    setDraftLabel("");
    setPending(null);
    setDraftType("text");
    setDraftRequired(false);
  };
  const moveField = (from, to) => {
    setFields((prev) => {
      if (from === to) return prev;
      const copy = [...prev];
      const [moved] = copy.splice(from, 1);
      copy.splice(to, 0, moved);
      return copy;
    });
  };

  const removeField = (index) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
  };

  const [, drop] = useDrop({
    accept: ItemTypes.PALETTE,
    drop: (item) => {
      setPending(item);
      setDraftLabel(item.label);
      setDraftType("text");
      setDraftRequired(false);
      setDialogOpen(true);
    },
  });

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 4,
          gap: 4,
          width: "100%",
        }}
      >
        <Paper
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 3,
            width: "100%",
            mx: "auto",
          }}
          elevation={1}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ flexGrow: 1, textAlign: "center" }}
            >
              Build Your Own Custom Form
            </Typography>

            {fields.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateForm}
              >
                Create Form
              </Button>
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Enter form name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
              sx={{ flex: 1 }}
            />
            <FormControl variant="outlined" sx={{ flex: 1 }}>
              <InputLabel id="form-type-label">Form Type</InputLabel>
              <Select
                labelId="form-type-label"
                value={draftType}
                onChange={(e) => setDraftType(e.target.value)}
                label="Form Type"
              >
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="number">Number</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField
            label="Enter form description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            multiline
            rows={4}
            fullWidth
          />
        </Paper>
        <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
          <Paper sx={{ p: 3, bgcolor: "grey.200" }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ flexGrow: 1, textAlign: "center" }}
              >
                Fields Palette
              </Typography>
              <Tooltip title="Select a field and drop it into the form area">
                <InfoIcon
                  size={20}
                  style={{ cursor: "pointer", flexShrink: 0 }}
                />
              </Tooltip>
            </Box>
            {templates.map((t, i) => (
              <DraggablePaletteItem key={i} tmpl={t} />
            ))}
          </Paper>

          {/* The actual drop area + form preview */}
          <Paper
            ref={drop}
            sx={{
              flex: 1,
              p: 2,
              border: "2px dashed",
              borderColor: "grey.300",
            }}
            elevation={1}
          >
            <FormLayout
              fields={fields}
              onSubmit={handleCreateForm}
              moveField={moveField}
              removeField={removeField}
            />
          </Paper>
        </Box>
      </Box>

      <FieldConfigDialog
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        draftLabel={draftLabel}
        setDraftLabel={setDraftLabel}
        draftType={draftType}
        setDraftType={setDraftType}
        draftRequired={draftRequired}
        setDraftRequired={setDraftRequired}
        addField={addField}
        pending={pending}
      ></FieldConfigDialog>
    </>
  );
}
